import { compareOrganisationNames } from "@/utils/organisationSort";
import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { CountrySelect } from "@/components/ui/country-select";
import { useCreateContact } from "@/hooks/use-contacts";
import { useStaff } from "@/hooks/use-staff";
import { usePartners } from "@/hooks/use-partners";
import { useOrganizations, useCreateCompany } from "@/hooks/use-companies";
import { supabase } from "@/integrations/supabase/client";
import { Search, UserPlus, UserCheck, User, Shield, Crown, Building2, X, Pencil } from "lucide-react";
import { ContactRole } from "@/types/contact";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useRoleBasedAccess, usePartnerOrganizations } from "@/hooks/use-role-based-access";
import { INDUSTRY_OPTIONS } from "@/constants/organization";

const contactSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email address"),
  phone: z.string().optional(),
  job_title: z.string().optional(),
  country: z.string().optional(),
  contact_type: z.enum(['Buyer', 'Influencer', 'Industry Expert']),
  relationship_status: z.enum(['Prospect', 'Customer']).optional(),
  notes: z.string().optional(),
  organization_id: z.string().min(1, "Organization is required"),
  internal_owner_id: z.string().optional(),
  external_owner_id: z.string().optional(),
  intended_system_role: z.enum(['standard', 'admin', 'editor']).optional(),
  is_company_admin: z.boolean().optional(),
});

interface ContactModalProps {
  prefilledOrganizationId?: string;
  onContactCreated?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  triggerButton?: React.ReactNode;
}

export const ContactModal = ({ 
  prefilledOrganizationId, 
  onContactCreated, 
  open: controlledOpen, 
  onOpenChange: controlledOnOpenChange,
  triggerButton
}: ContactModalProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = controlledOnOpenChange ?? setInternalOpen;
  const [isCreatingOrg, setIsCreatingOrg] = useState(false);
  const [orgSearchTerm, setOrgSearchTerm] = useState("");
  const [orgSelectOpen, setOrgSelectOpen] = useState(false);
  const [orgData, setOrgData] = useState({
    name: "",
    industry: "",
    address: "",
    country: "",
  });
  
  const queryClient = useQueryClient();
  const createContact = useCreateContact();
  const createCompany = useCreateCompany({ syncToBuildhip: true });
  const { data: staff = [] } = useStaff();
  const { data: partners = [] } = usePartners();
  const { data: organizations = [] } = useOrganizations();
  const { user } = useAuth();
  const { toast } = useToast();
  const { userOrganizationId, isSystemAdmin, isPartner } = useRoleBasedAccess();
  const { data: partnerOrganizations = [] } = usePartnerOrganizations();

  // Filter organizations based on user role
  const filteredOrganizations = useMemo(() => {
    // Superusers and internal staff see all organizations
    if (isSystemAdmin) {
      return organizations;
    }

    // Partners only see organizations reached through their own contacts/deals
    if (isPartner) {
      const partnerOrgIds = new Set((partnerOrganizations || []).map((org: any) => org.id));
      return organizations.filter(org => partnerOrgIds.has(org.id));
    }
    
    // Admins and standard users only see their own organization
    if (userOrganizationId) {
      return organizations.filter(org => org.id === userOrganizationId);
    }
    
    // Fallback: return empty array if no organization ID
    return [];
  }, [organizations, isSystemAdmin, isPartner, partnerOrganizations, userOrganizationId]);

  // Sort organizations alphanumerically and filter by the dropdown search bar
  const visibleOrganizations = useMemo(() => {
    const sorted = [...filteredOrganizations].sort((a, b) =>
      compareOrganisationNames(a.name || "", b.name || "")
    );
    const searchLower = orgSearchTerm.toLowerCase();
    return sorted.filter(org =>
      (org.name || "").toLowerCase().includes(searchLower)
    );
  }, [filteredOrganizations, orgSearchTerm]);

  const form = useForm<z.infer<typeof contactSchema>>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      job_title: "",
      country: "",
      contact_type: "Buyer",
      relationship_status: "Prospect",
      notes: "",
      organization_id: prefilledOrganizationId || "",
      internal_owner_id: undefined,
      external_owner_id: undefined,
      intended_system_role: "standard",
      is_company_admin: false,
    },
  });

  const onSubmit = async (values: z.infer<typeof contactSchema>) => {
    console.log("🔍 Form submission values:", values);
    console.log("🔍 Country value from form:", values.country);
    console.log("🔍 Country value type:", typeof values.country);
    
    // Auto-assign owner if none selected and current user is staff/partner
    let finalInternalOwnerId = values.internal_owner_id;
    let finalExternalOwnerId = values.external_owner_id;
    
    // If no owner is selected, try to auto-assign based on current user
    if (!finalInternalOwnerId && !finalExternalOwnerId && user) {
      // Strategy 1: Check if current user is staff
      const currentStaff = staff.find(s => s.user_id === user.id);
      if (currentStaff) {
        finalInternalOwnerId = currentStaff.id;
        console.log("🔍 Auto-assigned internal owner (staff):", currentStaff.id);
      } else {
        // Strategy 2: Check if current user is partner
        const currentPartner = partners.find(p => p.user_id === user.id);
        if (currentPartner) {
          finalExternalOwnerId = currentPartner.id;
          console.log("🔍 Auto-assigned external owner (partner):", currentPartner.id);
        } else {
          // Strategy 3: Inherit organization owner for org admins
          if (values.organization_id) {
            const selectedOrg = organizations.find(o => o.id === values.organization_id);
            if (selectedOrg) {
              if (selectedOrg.internal_owner_id) {
                finalInternalOwnerId = selectedOrg.internal_owner_id;
                console.log("🔍 Inherited internal owner from organization:", selectedOrg.internal_owner_id);
              } else if (selectedOrg.external_owner_id) {
                finalExternalOwnerId = selectedOrg.external_owner_id;
                console.log("🔍 Inherited external owner from organization:", selectedOrg.external_owner_id);
              }
            }
          }
        }
      }
    }
    
    // Strategy 4: Ultimate fallback - Roger Poeth (if organization has no owner)
    if (!finalInternalOwnerId && !finalExternalOwnerId) {
      const ROGER_POETH_STAFF_ID = 'de72e912-0784-4ab9-9f78-aa03cb15a3f0';
      finalInternalOwnerId = ROGER_POETH_STAFF_ID;
      console.log("🔍 Using Roger Poeth as fallback owner (organization has no owner)");
    }
    
    // Ensure required fields are properly set
    const contactData: any = {
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email,
      phone: values.phone || undefined,
      job_title: values.job_title || undefined,
      country: values.country || undefined,
      contact_type: values.contact_type,
      relationship_status: values.relationship_status || 'Prospect',
      notes: values.notes || undefined,
      organization_id: values.organization_id || undefined,
      internal_owner_id: finalInternalOwnerId || undefined,
      external_owner_id: finalExternalOwnerId || undefined,
      intended_system_role: values.intended_system_role || 'standard',
    };

    // If partner is creating this contact, set ownership status to pending
    if (isPartner) {
      contactData.partner_ownership_status = 'pending';
    }

    console.log("🔍 Contact data being sent:", contactData);
    console.log("🔍 Contact data country:", contactData.country);

    // If company admin is checked, update organization owner
    if (values.is_company_admin && values.organization_id) {
      // Get current user to determine if they're staff or partner
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: staffRecord } = await supabase
          .from('crm_staff')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
          
        const { data: partnerRecord } = await supabase
          .from('crm_partners')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        // Update organization with admin assignment
        if (staffRecord) {
          await supabase
            .from('organizations')
            .update({ internal_owner_id: staffRecord.id })
            .eq('id', values.organization_id);
        } else if (partnerRecord) {
          await supabase
            .from('organizations')
            .update({ external_owner_id: partnerRecord.id })
            .eq('id', values.organization_id);
        }
      }
    }

    // Pass intended_system_role directly - no field conversion needed
    createContact.mutate(contactData, {
      onSuccess: () => {
        setOpen(false);
        form.reset();
        onContactCreated?.();
      },
    });
  };

  const selectedOrgId = form.watch("organization_id");
  const hasOrgSelected = selectedOrgId && selectedOrgId !== "none" && selectedOrgId !== "create-new";
  const selectedUserRole = form.watch("intended_system_role");
  const isCompanyAdmin = form.watch("is_company_admin");

  // Handle organization selection
  const handleOrgChange = (value: string) => {
    if (value === "create-new") {
      setIsCreatingOrg(true);
      form.setValue("organization_id", "");
    } else {
      setIsCreatingOrg(false);
      form.setValue("organization_id", value);
    }
    setOrgSearchTerm("");
    setOrgSelectOpen(false);
  };

  // Duplicate organization detection
  const matchingOrganizations = useMemo(() => {
    if (!orgData.name.trim() || orgData.name.trim().length < 2) return [];
    const searchName = orgData.name.trim().toLowerCase();
    return filteredOrganizations.filter(org => 
      org.name?.toLowerCase().includes(searchName) || 
      searchName.includes(org.name?.toLowerCase() || '')
    );
  }, [orgData.name, filteredOrganizations]);

  // Create organization and then set it as selected
  const handleCreateOrganization = async () => {
    if (!orgData.name.trim()) return;
    
    try {
      // Get current user to set as owner
      const { data: { user } } = await supabase.auth.getUser();
      let ownerData = {};
      
      if (user) {
        // Check if user is staff
        const { data: staffRecord } = await supabase
          .from('crm_staff')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
          
        // Check if user is partner  
        const { data: partnerRecord } = await supabase
          .from('crm_partners')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
          
        if (staffRecord) {
          ownerData = { internal_owner_id: staffRecord.id };
        } else if (partnerRecord) {
          ownerData = { external_owner_id: partnerRecord.id };
        }
      }
      
      const newOrg = await createCompany.mutateAsync({
        name: orgData.name,
        industry: orgData.industry || undefined,
        address: orgData.address || undefined,
        country: orgData.country || undefined,
        ...ownerData,
      });
      
      // Invalidate and refetch organizations to ensure dropdown is updated
      await queryClient.invalidateQueries({ queryKey: ['organizations'] });
      await queryClient.invalidateQueries({ queryKey: ['partner-organizations'] });
      
      // Set the newly created organization as selected
      form.setValue("organization_id", newOrg.id);
      setIsCreatingOrg(false);
      setOrgData({ name: "", industry: "", address: "", country: "" });
      
      // Show success message
      toast({
        title: "Organization Created",
        description: `${newOrg.name} has been created and selected.`,
      });
    } catch (error) {
      console.error("Failed to create organization:", error);
      toast({
        title: "Error",
        description: "Failed to create organization. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Cancel organization creation
  const handleCancelOrgCreation = () => {
    setIsCreatingOrg(false);
    setOrgData({ name: "", industry: "", address: "", country: "" });
    form.setValue("organization_id", "");
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <Shield className="h-4 w-4" />;
      case 'editor':
        return <Pencil className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const getRoleDescription = (role: string) => {
    switch (role) {
      case 'admin':
        return "Can manage organization settings and other users";
      case 'editor':
        return "Standard user with additional edit rights";
      default:
        return "Standard access to organization features";
    }
  };

  // Reset all states when modal is closed
  const handleModalChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setIsCreatingOrg(false);
      setOrgSearchTerm("");
      setOrgSelectOpen(false);
      setOrgData({ name: "", industry: "", address: "", country: "" });
      form.reset();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleModalChange}>
      {triggerButton && (
        <DialogTrigger asChild>
          {triggerButton}
        </DialogTrigger>
      )}
      {!triggerButton && (
        <DialogTrigger asChild>
          <Button className="text-black hover:opacity-80" style={{backgroundColor: '#DFEF97'}}>
            <UserPlus className="h-4 w-4 mr-2" />
            Add Contact
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="bg-component-background -mx-6 -mt-6 px-6 py-4 rounded-t-xl border-b">
          <DialogTitle className="text-foreground">Add New Contact</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="John" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="john.doe@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input placeholder="+44 7700 900000" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="job_title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Job Title</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., CEO" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            {isSystemAdmin ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="contact_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Type *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select contact type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Buyer">Buyer</SelectItem>
                          <SelectItem value="Influencer">Influencer</SelectItem>
                          <SelectItem value="Industry Expert">Industry Expert</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="relationship_status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Relationship Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Prospect">Prospect</SelectItem>
                          <SelectItem value="Customer">Customer</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ) : (
              <FormField
                control={form.control}
                name="contact_type"
                render={({ field }) => (
                  <FormItem className="hidden">
                    <FormControl>
                      <Input type="hidden" {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}
            
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Country</FormLabel>
                  <FormControl>
                    <CountrySelect 
                      value={field.value} 
                      onValueChange={(value) => {
                        console.log("🔍 CountrySelect onValueChange called with:", value);
                        console.log("🔍 Value type:", typeof value);
                        field.onChange(value);
                      }}
                      placeholder="Select country"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="organization_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Organization *</FormLabel>
                  <Select
                    onValueChange={handleOrgChange}
                    value={field.value || undefined}
                    open={orgSelectOpen}
                    onOpenChange={(open) => {
                      setOrgSelectOpen(open);
                      if (!open) setOrgSearchTerm("");
                    }}
                  >
                    <FormControl>
                      <SelectTrigger className="border-teal-light ring-2 ring-teal-extraLight">
                        <SelectValue placeholder="Select an organization (required)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="max-h-[300px]">
                      <div className="sticky top-0 z-10 bg-background border-b p-2">
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                          <Input
                            placeholder="Search organizations..."
                            value={orgSearchTerm}
                            onChange={(e) => setOrgSearchTerm(e.target.value)}
                            className="pl-8 h-8"
                          />
                        </div>
                      </div>
                      <div className="max-h-[200px] overflow-y-auto">
                        {visibleOrganizations.length > 0 ? (
                          visibleOrganizations.map((org) => (
                            <SelectItem key={org.id} value={org.id}>
                              {org.name}
                            </SelectItem>
                          ))
                        ) : (
                          <div className="p-2 text-center text-muted-foreground text-sm">
                            No organizations found
                          </div>
                        )}
                        {(isSystemAdmin || isPartner) && (
                          <SelectItem value="create-new" className="text-primary font-medium">
                            + Create New Organization
                          </SelectItem>
                        )}
                      </div>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                  {!isCreatingOrg && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Each contact must be assigned to an organization for proper data management
                    </p>
                  )}
                </FormItem>
              )}
            />

            {/* Organization Creation Form */}
            {isCreatingOrg && (
              <div className="border-2 border-teal-light rounded-lg p-4 bg-teal-extraLight">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-teal-primary" />
                    <h3 className="font-medium text-teal-700">Create New Organization</h3>
                  </div>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleCancelOrgCreation}
                    className="h-8 w-8 p-0 text-teal-primary hover:bg-teal-extraLight"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-teal-700 mb-1">
                      Organization Name *
                    </label>
                    <Input
                      placeholder="Enter organization name"
                      value={orgData.name}
                      onChange={(e) => setOrgData({ ...orgData, name: e.target.value })}
                      className="border-teal-300"
                    />
                    {/* Duplicate organization warning */}
                    {matchingOrganizations.length > 0 && (
                      <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm font-medium text-amber-800 mb-2">
                          ⚠️ Similar organizations already exist:
                        </p>
                        <div className="space-y-1">
                          {matchingOrganizations.slice(0, 5).map((org) => (
                            <div key={org.id} className="flex items-center justify-between">
                              <span className="text-sm text-amber-700">{org.name}</span>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                                onClick={() => {
                                  form.setValue("organization_id", org.id);
                                  setIsCreatingOrg(false);
                                  setOrgData({ name: "", industry: "", address: "", country: "" });
                                }}
                              >
                                Use Existing
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-teal-700 mb-1">
                        Industry
                      </label>
                      <Select
                        value={orgData.industry}
                        onValueChange={(value) => setOrgData({ ...orgData, industry: value })}
                      >
                        <SelectTrigger className="border-teal-300">
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                        <SelectContent>
                          {INDUSTRY_OPTIONS.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                     <div>
                       <label className="block text-sm font-medium text-teal-700 mb-1">
                         Country
                       </label>
                       <CountrySelect
                         value={orgData.country}
                         onValueChange={(value) => setOrgData({ ...orgData, country: value })}
                         className="border-teal-300"
                       />
                     </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-teal-700 mb-1">
                      Address
                    </label>
                    <Input
                      placeholder="Organization address"
                      value={orgData.address}
                      onChange={(e) => setOrgData({ ...orgData, address: e.target.value })}
                      className="border-teal-300"
                    />
                  </div>
                  
                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleCancelOrgCreation}
                      className="text-teal-600 border-teal-300 hover:bg-teal-100"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleCreateOrganization}
                      disabled={!orgData.name.trim() || createCompany.isPending}
                      className="text-black hover:opacity-80"
                      style={{backgroundColor: '#DFEF97'}}
                    >
                      {createCompany.isPending ? "Creating..." : "Create Organization"}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Company Admin Assignment */}
            {hasOrgSelected && (
              <FormField
                control={form.control}
                name="is_company_admin"
                render={({ field }) => (
                  <FormItem className="border-2 border-amber-200 rounded-lg p-4 bg-amber-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Crown className="h-5 w-5 text-amber-600" />
                        <FormLabel className="text-amber-700 font-medium">Company Admin Assignment</FormLabel>
                      </div>
                      <FormControl>
                        <input
                          type="checkbox"
                          checked={field.value || false}
                          onChange={(e) => field.onChange(e.target.checked)}
                          className="h-4 w-4 text-amber-600 border-amber-300 rounded focus:ring-amber-500"
                        />
                      </FormControl>
                    </div>
                    <p className="text-xs text-amber-700 mt-2">
                      Check this box to assign this contact as an admin for the selected organization. This will update the organization's ownership records.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* System Access Level */}
            <FormField
              control={form.control}
              name="intended_system_role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    System Access Level
                    <Badge variant="outline" className="text-xs">
                      Future Account Role
                    </Badge>
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select system access level" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="standard">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          <span>Standard User</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="editor">
                        <div className="flex items-center gap-2">
                          <Pencil className="h-4 w-4" />
                          <span>Standard User with Edit Rights</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="admin">
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4" />
                          <span>System Admin</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      {getRoleIcon(selectedUserRole || 'standard')}
                      <span className="text-sm font-medium capitalize">{selectedUserRole || 'standard'} Access</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {getRoleDescription(selectedUserRole || 'standard')}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      This access level will be assigned when the contact creates a user account
                    </p>
                  </div>
                </FormItem>
              )}
            />

            {isSystemAdmin && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="internal_owner_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Internal Owner (Staff)</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || undefined}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select staff member" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">No staff owner</SelectItem>
                          {staff.map((member) => (
                            <SelectItem key={member.id} value={member.id}>
                              {member.first_name} {member.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="external_owner_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>External Owner (Partner)</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || undefined}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select partner" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">No partner owner</SelectItem>
                          {partners.map((partner) => (
                            <SelectItem key={partner.id} value={partner.id}>
                              {partner.first_name} {partner.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
            
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Additional notes about the contact" className="min-h-[100px]" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createContact.isPending} className="text-black hover:opacity-80" style={{backgroundColor: '#DFEF97'}}>
                {createContact.isPending ? "Creating..." : "Create Contact"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
